import { requestJson } from "../../../api/http/httpClient";
import { readArray, readNullableString, readObject, readOneOf, readString } from "../../../api/http/jsonDecoding";
import { readThread } from "../../../api/threads/threadsApi";
import { listProjectPeople } from "../../../api/access/accessApi";
import { readStringList } from "../../../api/content/contentDecoders";
import type { ThreadOverview } from "../../../api/threads/threadDecoders";
import { latestUpdateRequest, type UpdateThread } from "./connectionState";

export interface SourceConnection {
  readonly connectionId: string;
  readonly sourceId: string;
  readonly sourceName: string;
  /** The upstream the source mirrors; `null` for uploads and pushes to DMW. */
  readonly sourceRemote: string | null;
  readonly corpusId: string;
  readonly branch: string;
  readonly host: string;
  readonly hostName?: string;
  readonly policy: "automatic" | "on_demand";
  readonly upstream: string | null;
  readonly upstreamAt: string | null;
  readonly imported: string | null;
  readonly importedAt: string | null;
  readonly importedSelection?: readonly string[] | null;
  readonly requests: readonly {
    readonly newCommit: string;
    readonly state: "pending" | "started" | "proposed" | "failed";
    readonly thread: string | null;
  }[];
}

export interface ConnectionStatusView {
  readonly connection: SourceConnection;
  readonly thread: UpdateThread | null;
}

function connectionPath(sourceId: string) {
  return `/sources/${encodeURIComponent(sourceId)}/connections` as const;
}

export async function listSourceConnections(projectId: string, signal: AbortSignal): Promise<readonly ConnectionStatusView[]> {
  const [sources, members] = await Promise.all([requestJson({
    method: "GET", path: `/projects/${encodeURIComponent(projectId)}/sources`, signal,
    decodeResponse: (body) => readArray(body, "sources", (value) => {
      const source = readObject(value, "source");
      return { id: readString(source, "repository_id"), name: readString(source, "name"), remote: readNullableString(source, "upstream_remote") };
    }),
  }), listProjectPeople(projectId, signal)]);
  const lists = await Promise.all(sources.map(async (source) => {
    const connections = await requestJson({
      method: "GET", path: connectionPath(source.id), signal,
      decodeResponse: (body) => readArray(body, "source connections", (value): SourceConnection => {
        const item = readObject(value, "connection");
        return {
          connectionId: readString(item, "connection_id"), sourceId: source.id, sourceName: source.name, sourceRemote: source.remote,
          corpusId: readString(item, "corpus_id"), branch: readString(item, "branch"), host: readString(item, "host"),
          hostName: members.find((member) => member.userId === readString(item, "host"))?.displayName ?? readString(item, "host"),
          policy: readOneOf(item, "policy", ["automatic", "on_demand"]),
          upstream: readNullableString(item, "upstream"), upstreamAt: readNullableString(item, "upstream_at"),
          imported: readNullableString(item, "imported"), importedAt: readNullableString(item, "imported_at"),
          importedSelection: item["imported_selection"] == null ? null : readStringList(item, "imported_selection"),
          requests: readArray(item["requests"], "update requests", (value) => {
            const request = readObject(value, "update request");
            return { newCommit: readString(request, "new_commit"), thread: readNullableString(request, "thread"),
              state: readOneOf(request, "state", ["pending", "started", "proposed", "failed"]) };
          }),
        };
      }),
    });
    return Promise.all(connections.map(async (connection): Promise<ConnectionStatusView> => {
      const request = latestUpdateRequest(connection);
      const thread = request?.state === "started" && request.thread !== null ? updateThreadOf(await readThread(request.thread, signal)) : null;
      return { connection, thread };
    }));
  }));
  return lists.flat();
}

/** The update Thread's status from its overview: the server's step progress, not only whether it is live. */
function updateThreadOf(overview: ThreadOverview): UpdateThread {
  return {
    status: overview.projection.display_status,
    threadState: overview.state,
    connectorOnline: overview.connectorOnline,
  };
}

/** Ask the connection to import its newest commit now: the update of an `on_demand` connection, or a first import. */
export async function requestSourceUpdate(connection: Pick<SourceConnection, "sourceId" | "connectionId">): Promise<void> {
  await requestJson({
    method: "POST", path: `${connectionPath(connection.sourceId)}/${encodeURIComponent(connection.connectionId)}/update`,
    decodeResponse: (body) => { readObject(body, "requested source update"); },
  });
}
